import { useEffect } from 'react';
import clsx from 'clsx';
import { useToastStore, type Toast as ToastModel } from '../stores/toast.store.js';
import { haptics } from '../services/telegram.js';
import { feedbackSound } from '../services/audio/feedback.js';
import { useT } from '../i18n/index.js';

/**
 * A toast, on its painted plate (toast-artwork.css).
 *
 * The plate, its socket and its close button are one picture
 * (`ui/toast/toast-plate.webp`); the tone only picks which gem sits in the
 * socket. The words are live, laid on the stone the picture left for them.
 */
function ToastCard({ toast }: { toast: ToastModel }) {
  const { t } = useT();
  const dismiss = useToastStore((state) => state.dismiss);

  useEffect(() => {
    haptics.notify(toast.tone === 'error' ? 'error' : toast.tone === 'success' ? 'success' : 'warning');
    // With the haptic, its sound (services/audio/feedback.ts). An info toast is
    // a note and stays silent; a quiet one already has its own sound.
    if (toast.quiet) return;
    if (toast.tone === 'success') feedbackSound.success();
    else if (toast.tone === 'error') feedbackSound.error();
  }, [toast.tone, toast.quiet]);

  useEffect(() => {
    if (toast.duration <= 0) return;
    const timer = window.setTimeout(() => dismiss(toast.id), toast.duration);
    return () => window.clearTimeout(timer);
  }, [toast.duration, toast.id, dismiss]);

  return (
    <div
      className={clsx('toast', `toast--${toast.tone}`, (toast.description?.length ?? 0) > 47 && 'toast--long')}
      role="status"
      aria-live="polite"
    >
      <span className="toast__gem" aria-hidden />
      <div className="toast__body">
        <p className="toast__title">{toast.title}</p>
        {toast.description ? <p className="toast__desc">{toast.description}</p> : null}
      </div>
      {/* The X is painted on its plate; this is its reach. */}
      <button type="button" className="toast__close" onClick={() => dismiss(toast.id)} aria-label={t('common.dismiss')} />
    </div>
  );
}

export function ToastViewport() {
  const toasts = useToastStore((state) => state.toasts);
  if (toasts.length === 0) return null;

  return (
    <div className="toast-viewport">
      {toasts.map((toast) => (
        <ToastCard key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
